import React, { useState, useEffect } from "react";
import {
  Table,
  Tag,
  Space,
  Button,
  Modal,
  Form,
  Input,
  TreeSelect,
  message,
  Checkbox,
} from "antd";

import {
  getRoles,
  createRole,
  updateRole,
  deleteRole,
} from "../../api/setting/Role";

import { getMenuPermissionsAction } from "../../api/setting/Permission";

const sessiData = JSON.parse(sessionStorage.getItem("user"));

const RoleAccessPage = () => {
  const [data, setData] = useState([]);
  const [isLoadingTable, setIsLoadingTable] = useState(false);
  const [menuOptions, setMenuOptions] = useState([]);
  const [menuButtons, setMenuButtons] = useState([]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [form] = Form.useForm();

  const columns = [
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
      filters: [...new Set(data.map((item) => item.role))].map((role) => ({
        text: role,
        value: role,
      })),
      onFilter: (value, record) => record.role === value,
    },
    {
      title: "Deskripsi",
      dataIndex: "roleDescription",
      key: "roleDescription",
    },
    {
      title: "View Access",
      key: "viewAccessMenu",
      render: (_, record) => (
        <div>
          {record.viewAccessMenu.map((menu) => renderMenuWithCheck(menu))}
        </div>
      ),
    },
    {
      title: "Button Access",
      key: "accessButtons",
      render: (_, record) => (
        <div>
          {menuButtons.map((btn) => (
            <div key={btn.value}>
              {record.accessButtons.includes(btn.label) ? "✅" : "❌"}{" "}
              {btn.label}
            </div>
          ))}
        </div>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Button type="link" onClick={() => showModal(record)}>
            Edit
          </Button>
          {record.role !== "Admin" && (
            <Button
              type="link"
              danger
              onClick={() => handleDelete(record.key, record.role)}
            >
              Delete
            </Button>
          )}
        </Space>
      ),
    },
  ];

  useEffect(() => {
    const normalizeMenuOptions = (menus) =>
      menus.map((menu) => ({
        ...menu,
        name: menu.title, // tambahkan name dari title
        children: menu.children ? normalizeMenuOptions(menu.children) : [],
      }));

    const fetchMenuAction = async () => {
      try {
        const rolesData = await getMenuPermissionsAction();
        const normalizedMenus = normalizeMenuOptions(
          rolesData.viewAccessMenu || []
        );
        setMenuOptions(normalizedMenus);

        const btn = rolesData.accessButtons?.map((item) => ({
          label: item.title,
          value: item.id,
        }));
        setMenuButtons(btn);
      } catch (error) {
        console.error("Failed to fetch menu actions:", error);
      }
    };

    fetchRoles();
    fetchMenuAction();
  }, []);

  useEffect(() => {
    if (editingRole && menuOptions.length > 0 && menuButtons.length > 0) {
      const selectedNames = flattenMenuAccess(editingRole.viewAccessMenu);
      const selectedMenuIds = findIdsFromNames(selectedNames, menuOptions);

      // Ubah accessButtons ke value
      const buttonValues = editingRole.accessButtons
        ?.map((label) => {
          const match = menuButtons.find((btn) => btn.label === label);
          return match?.value;
        })
        .filter(Boolean); // buang null

      form.setFieldsValue({
        role: editingRole.role,
        role_desc: editingRole.roleDescription,
        viewAccessMenu: selectedMenuIds,
        accessButtons: buttonValues,
      });
    }
  }, [editingRole, menuOptions, menuButtons]);

  const fetchRoles = async () => {
    setIsLoadingTable(true);
    try {
      const rolesData = await getRoles();
      const formattedData = rolesData.data.map((role) => ({
        key: role.key,
        role: role.role,
        roleDescription: role.roleDescription,
        viewAccessMenu: mapViewAccessMenu(role.viewAccessMenu),
        accessButtons: role.accessButtons,
      }));
      setIsLoadingTable(false);
      setData(formattedData);
    } catch (error) {
      setIsLoadingTable(false);
      message.error(`Failed to fetch roles: ` + error);
      console.error("Failed to fetch roles:", error);
    }
  };

  const showModal = (roleToEdit = null) => {
    setIsModalOpen(true);

    if (roleToEdit) {
      setEditingRole(roleToEdit);
    } else {
      setEditingRole(null);
      form.resetFields();
    }
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setEditingRole(null);
    form.resetFields();
  };

  const flattenMenuAccess = (menus) => {
    const result = new Set();

    menus.forEach((menu) => {
      const children = menu.children || [];

      // Jika menu punya children
      if (children.length > 0) {
        const allChildrenExist = children.every((child) =>
          menus.some((m) => m.name === child)
        );

        if (allChildrenExist) {
          result.add(menu.name); // ✅ semua anak dipilih → tambahkan parent
        } else {
          children.forEach((child) => result.add(child)); // ❌ hanya beberapa → hanya anak
        }
      } else {
        // Menu tanpa children
        result.add(menu.name);
      }
    });

    return Array.from(result);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const selectedIds = values.viewAccessMenu;
      const grouped = groupSelectedByParent(selectedIds, menuOptions);

      if (editingRole) {
        const newRole = {
          role: values.role,
          role_desc: values.role_desc ?? editingRole.roleDescription,
          role_menu: grouped,
          role_buttons: values.accessButtons || [],
          user_id: sessiData[0].user[0].userid,
          role_id: editingRole.key,
        };

        const resCreate = await updateRole(newRole);
        resCreate.status
          ? message.success(resCreate.message)
          : message.error(resCreate.message);
      } else {
        const newRole = {
          role: values.role,
          role_desc: values.role_desc ?? null,
          role_menu: grouped,
          role_buttons: values.accessButtons || [],
          user_id: sessiData[0].user[0].userid,
        };

        const resCreate = await createRole(newRole);
        resCreate.status
          ? message.success(resCreate.message)
          : message.error(resCreate.message);
      }

      handleCancel();
      fetchRoles();
    } catch (error) {
      //console.error("Error:", error);
      message.error(error.message || "Terjadi kesalahan");
    }
  };

  const handleDelete = (key, roleName) => {
    Modal.confirm({
      title: `Delete Role "${roleName}"?`,
      content: "Are you sure you want to delete this role?",
      okText: "Yes",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          const payload = {
            role_id: key,
          };
          const response = await deleteRole(payload);
          fetchRoles();
          response.status
            ? message.success(response.message)
            : message.error(response.message);
        } catch (error) {
          const errorMessage =
            error?.message || "Terjadi kesalahan saat menghapus data";
          message.error(errorMessage);
        }
      },
    });
  };

  const renderMenuWithCheck = (menu) => {
    return (
      <div key={menu.name} style={{ marginBottom: 4 }}>
        ✅ {menu.name}
        {menu.children && (
          <div style={{ paddingLeft: 24 }}>
            {menu.children.map((child) => (
              <div key={child}>✅ {child}</div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const mapViewAccessMenu = (menuItems) =>
    menuItems.map((item) => ({
      title: item.name,
      name: item.name,
      ...(item.children &&
        item.children.length > 0 && {
          children: item.children.map((child) => child.name),
        }),
    }));

  const buildParentMap = (nodes, parent = null, map = {}) => {
    nodes.forEach((node) => {
      map[node.id] = parent?.id ?? null;
      if (node.children) {
        buildParentMap(node.children, node, map);
      }
    });
    return map;
  };

  const groupSelectedByParent = (selectedIds, menuOptions) => {
    const parentMap = {};
    const nodeMap = {};

    // Build node and parent maps
    const buildMaps = (nodes, parentId = null) => {
      nodes?.forEach((node) => {
        nodeMap[node.id] = node;
        if (node.children) {
          node.children.forEach((child) => {
            parentMap[child.id] = node.id;
          });
          buildMaps(node.children, node.id);
        }
      });
    };

    buildMaps(menuOptions);

    const resultMap = {};

    selectedIds?.forEach((id) => {
      const parentId = parentMap[id];

      if (parentId) {
        // Push to corresponding parent
        if (!resultMap[parentId]) {
          resultMap[parentId] = {
            id: parentId,
            children: [],
          };
        }
        resultMap[parentId].children.push(id);
      } else {
        // Root-level (no parent): treat as parent with no children
        if (!resultMap[id]) {
          resultMap[id] = {
            id: id,
            children: [],
          };
        }
      }
    });

    // Return as array
    return Object.values(resultMap);
  };

  const findIdsFromNames = (names, nodes) => {
    const ids = [];

    const traverse = (items) => {
      items?.forEach((item) => {
        if (names.includes(item.name)) {
          ids.push(item.id);
        }
        if (item.children) {
          traverse(item.children);
        }
      });
    };

    traverse(nodes);
    return ids;
  };

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Button type="primary" onClick={() => showModal()}>
          + Tambah Role
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={data}
        loading={isLoadingTable}
        pagination={{
          pageSizeOptions: ["10", "20", "40", "60"],
          showSizeChanger: true,
          defaultPageSize: 20,
        }}
      />

      <Modal
        title={editingRole ? "Edit Role" : "Add Role"}
        open={isModalOpen}
        onOk={handleSave}
        onCancel={handleCancel}
        okText={editingRole ? "Save Changes" : "Add Role"}
      >
        <Form layout="vertical" form={form}>
          <Form.Item
            name="role"
            label="Role Name"
            rules={[{ required: true, message: "Please input role name!" }]}
          >
            <Input />
          </Form.Item>

          <Form.Item name="role_desc" label="Description">
            <Input />
          </Form.Item>

          <Form.Item name="viewAccessMenu" label="Accessible Menus">
            <TreeSelect
              treeData={menuOptions}
              treeCheckable
              treeCheckStrictly={false}
              showCheckedStrategy={TreeSelect.SHOW_CHILD}
              placeholder="Select accessible menus"
              style={{ width: "100%" }}
              allowClear
              multiple
              fieldNames={{
                label: "title",
                value: "id",
                children: "children",
              }}
            />
          </Form.Item>

          <Form.Item name="accessButtons" label="Button Access">
            <Checkbox.Group options={menuButtons} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default RoleAccessPage;
